/**
 * Sound Meter — Main application entry point.
 *
 * Orchestrates microphone capture, pink noise playback,
 * real-time spectrum analysis, measurement averaging,
 * EQ fitting, and APO preset export.
 */

import type { AppConfig, EqPreset } from "./types";
import { DEFAULT_CONFIG } from "./types";
import { startCapture, readFftData, stopCapture, type CaptureNode } from "./audio/capture";
import {
  initPinkNoiseWorklet,
  createPinkNoise,
  playPinkNoise,
  stopPinkNoise,
  setPinkNoiseLevel,
  type PinkNoiseNode,
} from "./audio/pink-noise";
import { generateBandCenters, buildBinMap, aggregateToBands, accumulateEnergy, finalizeAverage } from "./dsp/bands";
import { cWeightingCurve } from "./dsp/weighting";
import { smoothBands } from "./dsp/smoothing";
import { combinedMagnitudeDb, peakGain } from "./dsp/biquad";
import { fitEq } from "./dsp/eq-fit";
import { downloadApoPreset, downloadDebugData, type DebugData } from "./apo/export";
import { createChart, updateChart, type ChartSeries } from "./ui/chart";
import {
  getControlElements,
  setMicStatus,
  setNoiseState,
  setMeasureProgress,
  showError,
  type ControlElements,
} from "./ui/controls";

// ─── Application State ──────────────────────────────────────────────

let config: AppConfig = { ...DEFAULT_CONFIG };
let capture: CaptureNode | null = null;
let noiseContext: AudioContext | null = null;
let noiseNode: PinkNoiseNode | null = null;
let chart: uPlot | null = null;
let animFrame: number = 0;
let controls: ControlElements;

// Band analysis state
let bands = generateBandCenters(config.smoothingFraction);
let binMap: Int32Array[] = [];
let logFreqs: number[] = [];
let targetCurve: Float64Array = new Float64Array(0);

// Measurement state
let measuring = false;
let measureStart = 0;
let accumulator: Float64Array = new Float64Array(0);
let frameCount = { value: 0 };
let lastPreset: EqPreset | null = null;
let lastMeasured: Float64Array | null = null;
let lastTarget: Float64Array | null = null;

// ─── Initialize ─────────────────────────────────────────────────────

async function init(): Promise<void> {
  controls = getControlElements();
  initBands();
  initChart();
  bindEvents();
  await initNoise();
}

function initBands(): void {
  bands = generateBandCenters(config.smoothingFraction);
  logFreqs = bands.map((b) => Math.log10(b.center));
  const freqs = bands.map((b) => b.center);
  targetCurve = cWeightingCurve(freqs);
}

function initChart(): void {
  const container = document.getElementById("chart")!;
  chart = createChart(container, bands.map((b) => b.center), targetCurve);
}

/**
 * Shift the C-weighting target curve so its median matches the median
 * of the measured data. This makes the overlay meaningful on a dBFS scale
 * and ensures the EQ fitting corrects the *shape* without chasing the
 * absolute level difference.
 */
function normalizeTarget(measured: Float64Array): Float64Array {
  // Compute median of measured data (ignoring -Infinity)
  const valid = Array.from(measured).filter((v) => v > -120);
  if (valid.length === 0) return targetCurve;
  valid.sort((a, b) => a - b);
  const measuredMedian = valid[Math.floor(valid.length / 2)];

  // Compute median of raw target curve
  const targetValid = Array.from(targetCurve).filter((v) => v > -120);
  targetValid.sort((a, b) => a - b);
  const targetMedian = targetValid[Math.floor(targetValid.length / 2)];

  const offset = measuredMedian - targetMedian;
  const shifted = new Float64Array(targetCurve.length);
  for (let i = 0; i < targetCurve.length; i++) {
    shifted[i] = targetCurve[i] + offset;
  }
  return shifted;
}

function rebuildBands(): void {
  initBands();
  if (capture) {
    binMap = buildBinMap(bands, capture.analyser.fftSize, capture.context.sampleRate);
  }
  // Re-create chart with new bands
  const container = document.getElementById("chart")!;
  container.innerHTML = "";
  chart = createChart(container, bands.map((b) => b.center), targetCurve);
}

// ─── Pink Noise (independent of mic) ────────────────────────────────

async function initNoise(): Promise<void> {
  try {
    noiseContext = new AudioContext();
    await initPinkNoiseWorklet(noiseContext);
    noiseNode = createPinkNoise(noiseContext, config.noiseLevel);
  } catch (e) {
    console.warn("AudioWorklet not supported, pink noise disabled:", e);
    controls.btnNoise.disabled = true;
    controls.noiseLevel.disabled = true;
    controls.btnNoise.title = "AudioWorklet not supported in this browser";
  }
}

function toggleNoise(): void {
  if (!noiseContext || !noiseNode) return;

  // AudioContext may be suspended until a user gesture
  if (noiseContext.state === "suspended") {
    noiseContext.resume();
  }

  if (noiseNode.playing) {
    stopPinkNoise(noiseNode);
    setNoiseState(controls, false);
  } else {
    playPinkNoise(noiseNode, noiseContext);
    setNoiseState(controls, true);
  }
}

// ─── Microphone ─────────────────────────────────────────────────────

async function toggleMic(): Promise<void> {
  if (capture) {
    // Stop
    cancelAnimationFrame(animFrame);
    stopCapture(capture);
    capture = null;
    setMicStatus(controls, false);
    stopMeasurement();
    return;
  }

  try {
    capture = await startCapture();
    binMap = buildBinMap(bands, capture.analyser.fftSize, capture.context.sampleRate);
    setMicStatus(controls, true, capture.context.sampleRate);

    // Start the render loop
    renderLoop();
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Unknown error";
    if (msg.includes("Permission") || msg.includes("NotAllowed")) {
      showError("Microphone access denied. Please allow microphone access and try again.");
    } else {
      showError(`Microphone error: ${msg}`);
    }
  }
}

// ─── Measurement ────────────────────────────────────────────────────

function startMeasurement(): void {
  if (!capture) return;

  measuring = true;
  measureStart = performance.now();
  accumulator = new Float64Array(bands.length);
  frameCount = { value: 0 };
  lastPreset = null;
  controls.btnExport.disabled = true;
  controls.btnDebug.disabled = true;
  setMeasureProgress(controls, "capturing", 0, config.averagingTime);
}

function stopMeasurement(): void {
  if (!measuring) return;

  measuring = false;

  if (frameCount.value > 0) {
    // Finalize the measurement
    const averaged = finalizeAverage(accumulator, frameCount.value);
    const smoothed = smoothBands(averaged, bands, config.smoothingFraction);

    // Normalize the target curve to the measured level before fitting
    const normalizedTarget = normalizeTarget(smoothed);

    // Store for debug export
    lastMeasured = smoothed;
    lastTarget = normalizedTarget;

    // Fit EQ
    const freqs = bands.map((b) => b.center);
    lastPreset = fitEq(smoothed, normalizedTarget, bands, {
      maxBands: config.eqBandCount,
    });

    // Compute EQ curve and predicted response for display
    const eqCurve = new Float64Array(bands.length);
    const predicted = new Float64Array(bands.length);
    for (let i = 0; i < bands.length; i++) {
      eqCurve[i] = combinedMagnitudeDb(freqs[i], lastPreset.bands);
      predicted[i] = smoothed[i] + lastPreset.preamp + eqCurve[i];
    }

    // Update chart with final results
    const series: ChartSeries = {
      frequencies: freqs,
      live: null,
      averaged: smoothed,
      target: normalizedTarget,
      eqCurve,
      predicted,
    };
    if (chart) updateChart(chart, logFreqs, series);

    setMeasureProgress(controls, "complete");
  } else {
    setMeasureProgress(controls, "idle");
  }
}

function toggleMeasurement(): void {
  if (measuring) {
    stopMeasurement();
  } else {
    startMeasurement();
  }
}

// ─── Render Loop ────────────────────────────────────────────────────

function renderLoop(): void {
  if (!capture || !chart) return;

  const fftData = readFftData(capture);
  const bandData = aggregateToBands(fftData, binMap);
  const smoothed = smoothBands(bandData, bands, config.smoothingFraction);

  // If measuring, accumulate
  if (measuring) {
    accumulateEnergy(accumulator, bandData, frameCount);

    const elapsed = (performance.now() - measureStart) / 1000;
    setMeasureProgress(controls, "capturing", elapsed, config.averagingTime);

    // Auto-stop when averaging time is reached
    if (elapsed >= config.averagingTime) {
      stopMeasurement();
    }
  }

  // Update chart with live data (C-curve stays at its fixed reference)
  const series: ChartSeries = {
    frequencies: bands.map((b) => b.center),
    live: smoothed,
    averaged: null,
    target: targetCurve,
    eqCurve: null,
    predicted: null,
  };

  // If we have a completed measurement, keep showing it
  if (lastPreset && !measuring) {
    const finalAvg = finalizeAverage(
      accumulator,
      frameCount.value || 1
    );
    const finalSmoothed = smoothBands(finalAvg, bands, config.smoothingFraction);
    series.averaged = finalSmoothed;

    const freqs = bands.map((b) => b.center);
    const eqCurve = new Float64Array(bands.length);
    const predicted = new Float64Array(bands.length);
    for (let i = 0; i < bands.length; i++) {
      eqCurve[i] = combinedMagnitudeDb(freqs[i], lastPreset.bands);
      predicted[i] = finalSmoothed[i] + lastPreset.preamp + eqCurve[i];
    }
    series.eqCurve = eqCurve;
    series.predicted = predicted;
    series.target = targetCurve;
  }

  updateChart(chart, logFreqs, series);
  animFrame = requestAnimationFrame(renderLoop);
}

// ─── Event Bindings ─────────────────────────────────────────────────

function bindEvents(): void {
  controls.btnMic.addEventListener("click", toggleMic);
  controls.btnNoise.addEventListener("click", toggleNoise);
  controls.btnMeasure.addEventListener("click", toggleMeasurement);
  controls.btnExport.addEventListener("click", () => {
    if (lastPreset) downloadApoPreset(lastPreset);
  });

  controls.btnDebug.addEventListener("click", () => {
    if (lastPreset && lastMeasured && lastTarget) {
      const data: DebugData = {
        frequencies: bands.map((b) => b.center),
        measured: lastMeasured,
        target: lastTarget,
        preset: lastPreset,
        peakGain: peakGain(lastPreset.bands),
      };
      downloadDebugData(data);
    }
  });

  controls.noiseLevel.addEventListener("input", () => {
    const level = parseInt(controls.noiseLevel.value, 10);
    config.noiseLevel = level;
    controls.noiseLevelDisplay.textContent = `${level} dBFS`;
    if (noiseNode) setPinkNoiseLevel(noiseNode, level);
  });

  controls.avgTime.addEventListener("change", () => {
    config.averagingTime = parseInt(controls.avgTime.value, 10);
  });

  controls.smoothing.addEventListener("change", () => {
    config.smoothingFraction = parseInt(controls.smoothing.value, 10);
    rebuildBands();
  });

  controls.eqBands.addEventListener("change", () => {
    const val = parseInt(controls.eqBands.value, 10);
    config.eqBandCount = Math.max(1, Math.min(20, val));
  });
}

// ─── Boot ───────────────────────────────────────────────────────────

document.addEventListener("DOMContentLoaded", init);
